import { useEffect, useState, useCallback } from 'react'
import { Box, Button, Checkbox, FormControl, FormErrorMessage, FormLabel, Grid, HStack, Input, VStack } from '@chakra-ui/react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

export type SlaFormProps = { slaId?: number; onSuccess?: () => void; onCancel?: () => void }

const initialValues = {
  severity: '',
  response_minutes: '' as string | number,
  resolution_minutes: '' as string | number,
  pause_on_onhold: true,
  pause_on_fe_visit: true,
}

export function SlaForm({ slaId, onSuccess, onCancel }: SlaFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(!!slaId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState(initialValues)

  const loadOne = useCallback(async () => {
    if (!slaId) return
    setLoading(true)
    setErrors({})
    try {
      const { data } = await apiClient.get(`/service-catalog/sla-timing/${slaId}`)
      const r = data?.data ?? data
      setForm({
        severity: r.severity ?? '',
        response_minutes: r.response_minutes ?? '',
        resolution_minutes: r.resolution_minutes ?? '',
        pause_on_onhold: r.pause_on_onhold !== false,
        pause_on_fe_visit: r.pause_on_fe_visit !== false,
      })
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [slaId, showError])

  useEffect(() => {
    if (slaId) loadOne()
    else setForm(initialValues)
  }, [slaId, loadOne])

  const updateField = (field: keyof typeof form, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors((prev) => { const next = { ...prev }; delete next[field]; return next })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      const payload = {
        severity: form.severity.trim() || null,
        response_minutes: form.response_minutes === '' ? null : Number(form.response_minutes),
        resolution_minutes: form.resolution_minutes === '' ? null : Number(form.resolution_minutes),
        pause_on_onhold: form.pause_on_onhold,
        pause_on_fe_visit: form.pause_on_fe_visit,
      }
      if (slaId) {
        await apiClient.put(`/service-catalog/sla-timing/${slaId}`, payload)
        showSuccess('Success', 'SLA updated successfully')
      } else {
        await apiClient.post('/service-catalog/sla-timing', payload)
        showSuccess('Success', 'SLA created successfully')
      }
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') setErrors(res.errors)
      else showError(err as Error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Box py={8}>Loading...</Box>

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Severity</FormLabel>
          <FormControl isInvalid={!!errors.severity?.length}>
            <Input value={form.severity} onChange={(e) => updateField('severity', e.target.value)} placeholder="e.g. 1–5, high, medium, low" />
            <FormErrorMessage>{errors.severity?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Response (minutes)</FormLabel>
          <FormControl isInvalid={!!errors.response_minutes?.length}>
            <Input type="number" min={0} value={form.response_minutes === '' ? '' : form.response_minutes} onChange={(e) => updateField('response_minutes', e.target.value === '' ? '' : e.target.value)} placeholder="Optional" />
            <FormErrorMessage>{errors.response_minutes?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Resolution (minutes)</FormLabel>
          <FormControl isInvalid={!!errors.resolution_minutes?.length}>
            <Input type="number" min={0} value={form.resolution_minutes === '' ? '' : form.resolution_minutes} onChange={(e) => updateField('resolution_minutes', e.target.value === '' ? '' : e.target.value)} placeholder="Optional" />
            <FormErrorMessage>{errors.resolution_minutes?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>Pause on on-hold</FormLabel>
          <FormControl>
            <Checkbox isChecked={form.pause_on_onhold} onChange={(e) => updateField('pause_on_onhold', e.target.checked)} colorScheme="blue">Pause SLA when ticket is on hold.</Checkbox>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>Pause on FE visit</FormLabel>
          <FormControl>
            <Checkbox isChecked={form.pause_on_fe_visit} onChange={(e) => updateField('pause_on_fe_visit', e.target.checked)} colorScheme="blue">Pause SLA on field engineer visit.</Checkbox>
          </FormControl>
        </Grid>
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && <Button variant="outline" onClick={onCancel} isDisabled={saving}>Cancel</Button>}
          <Button type="submit" colorScheme="blue" isLoading={saving} loadingText={slaId ? 'Saving...' : 'Creating...'}>{slaId ? 'Save' : 'Create SLA'}</Button>
        </HStack>
      </VStack>
    </Box>
  )
}
